import React from "react";
import { Button } from "reactstrap";
import { formatAmountRounded } from "../../helpers/currencyConversion";

/**
 * DebtCategoryFilter
 *
 * Renderiza una fila de chips (botones) para filtrar deudas por categoría.
 * Solo renderiza categorías con pending > 0.
 *
 * Props:
 *   categories: [{ category, label, pending }, ...]
 *   value: category string ("all", "ordinary", "particular", "reserve_fund", etc.)
 *   onChange: (category: string) => void
 *   currency: string (ej. "USD")
 */
const DebtCategoryFilter = ({ categories, value, onChange, currency = "USD" }) => {
  if (!categories || categories.length === 0) {
    return null;
  }

  // Chip "Todo" con suma de todos los pending
  const totalPending = categories.reduce((sum, c) => sum + (c.pending || 0), 0);

  // Se muestran todas las categorías del período, incluidas las ya saldadas:
  // que una desaparezca al terminar de pagarla deja la duda de si se pagó o si
  // el filtro falló. Las saldadas quedan a la vista, marcadas y sin acción.
  const visibleCategories = categories.filter(
    (c) => c.pending > 0 || c.paid > 0
  );

  if (visibleCategories.length === 0) {
    return null;
  }

  const isSettled = (cat) => cat.pending <= 0.005 && cat.paid > 0;

  return (
    <div className="debt-category-filter mb-3">
      <small className="text-muted d-block mb-2">Pagar por categoría:</small>
      <div className="d-flex flex-wrap" style={{ gap: "0.5rem" }}>
        {/* Chip "Todo" */}
        <Button
          size="sm"
          color={value === "all" ? "primary" : "outline-primary"}
          onClick={() => onChange("all")}
          className="rounded-pill"
          style={{
            padding: "0.4rem 0.8rem",
            fontSize: "0.8rem",
            fontWeight: "500",
            whiteSpace: "nowrap",
          }}
        >
          {value === "all" ? "✓ " : ""}
          Todo
        </Button>

        {/* Chips por categoría */}
        {visibleCategories.map((cat) => {
          const settled = isSettled(cat);

          return (
            <Button
              key={cat.category}
              size="sm"
              color={
                settled
                  ? "outline-success"
                  : value === cat.category
                  ? "primary"
                  : "outline-primary"
              }
              onClick={() => !settled && onChange(cat.category)}
              disabled={settled}
              title={settled ? "Esta categoría ya está pagada" : undefined}
              className="rounded-pill"
              style={{
                padding: "0.4rem 0.8rem",
                fontSize: "0.8rem",
                fontWeight: "500",
                whiteSpace: "nowrap",
                cursor: settled ? "default" : "pointer",
                opacity: settled ? 0.75 : 1,
              }}
            >
              {settled ? "✓ " : value === cat.category ? "✓ " : ""}
              {cat.label}
              {settled && (
                <span className="ml-1" style={{ fontSize: "0.7rem" }}>
                  (pagado)
                </span>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default DebtCategoryFilter;
